import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppFrame, TopBar, BottomNav } from "@/components/Shell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, Loader2 } from "lucide-react";
import CategoryTreePicker from "@/components/CategoryTreePicker";
import { compressImage } from "@/lib/image";

const MAX_FILES = 50;

export default function BulkUpload() {
  const nav = useNavigate();
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catId, setCatId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef();

  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    (async () => setCategories((await api.get("/categories")).data))();
  }, []);

  const addFiles = async (list) => {
    const arr = Array.from(list || []).slice(0, MAX_FILES - files.length);
    if (arr.length === 0) return;
    setPreparing(true);
    try {
      // Compress in parallel — makes huge phone photos upload-ready
      const compressed = await Promise.all(arr.map((f) => compressImage(f)));
      setFiles((prev) => [
        ...prev,
        ...compressed.map((f) => ({ file: f, preview: URL.createObjectURL(f) })),
      ]);
    } finally {
      setPreparing(false);
    }
  };

  const remove = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const upload = async () => {
    if (files.length === 0) return toast.error("Add at least one image");
    if (!catId) return toast.error("Pick a category first");
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      files.forEach((it) => fd.append("files", it.file));
      fd.append("category_id", catId);
      const { data } = await api.post("/products/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const msg = data.skipped
        ? `${data.count} drafts created · ${data.skipped} file(s) skipped (invalid or > 8 MB)`
        : `${data.count} draft product${data.count === 1 ? "" : "s"} created`;
      toast.success(msg);
      nav("/admin/drafts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppFrame>
      <TopBar title="Bulk Upload" subtitle="Studio" />
      <div className="px-5 mt-4 space-y-4">
        <div>
          <label className="text-[9px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>Category (required)</label>
          <div className="mt-1">
            <CategoryTreePicker
              categories={categories}
              value={catId}
              onChange={(id) => setCatId(id)}
              placeholder="Choose a category for these drafts"
            />
          </div>
        </div>

        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          data-testid="bulk-dropzone"
          className="rounded-2xl border-2 border-dashed py-10 text-center press cursor-pointer"
          style={{ borderColor: "rgba(27,25,23,0.15)", background: "var(--bg-alt)" }}
        >
          <UploadCloud size={26} strokeWidth={1.4} className="mx-auto" style={{ color: "var(--ink-2)" }} />
          <div className="font-display text-[16px] mt-2">
            {preparing ? "Preparing photos…" : "Drop up to 50 images"}
          </div>
          <div className="text-[11px] mt-1" style={{ color: "var(--ink-3)" }}>
            {preparing ? "Compressing for fast upload" : `or tap to browse · ${files.length}/${MAX_FILES} selected`}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />

        {files.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {files.map((it, i) => (
              <div key={i} data-testid={`bulk-preview-${i}`} className="relative aspect-square rounded-xl overflow-hidden">
                <img src={it.preview} alt="" className="w-full h-full object-cover" />
                <button
                  data-testid={`bulk-remove-${i}`}
                  onClick={() => remove(i)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full grid place-items-center press"
                  style={{ background: "rgba(244,237,226,0.9)", color: "var(--ink)" }}
                ><X size={11} /></button>
              </div>
            ))}
          </div>
        )}

        {uploading && (
          <div className="rounded-xl p-3" style={{ background: "var(--bg-alt)" }}>
            <div className="text-[10px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>Uploading… {progress}%</div>
            <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(27,25,23,0.08)" }}>
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: "var(--accent)" }} />
            </div>
          </div>
        )}

        <button
          data-testid="bulk-upload-btn"
          onClick={upload}
          disabled={uploading || files.length === 0 || !catId}
          className="w-full h-12 rounded-full text-[12px] tracking-editorial-sm uppercase press disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ background: "var(--ink)", color: "var(--bg)" }}
        >
          {uploading ? <><Loader2 className="animate-spin" size={14} /> Uploading…</> : `Create ${files.length} draft${files.length === 1 ? "" : "s"}`}
        </button>
      </div>
      <BottomNav />
    </AppFrame>
  );
}
